/**
 * Create a warehouse without leaving the form that needs one (#268, UX-review
 * finding 4): the store product form's Inventory card dead-ended on
 * "No warehouses yet". Same five fields as `/inventory/warehouses/new`
 * (shared `WarehouseFormFields`), same `warehouses.create` mutation and
 * permission gate (`inventory.manage` — the caller hides the trigger without
 * it, and the server enforces it regardless).
 *
 * Mount only while open (the pricing-dialog rule): each open seeds a fresh
 * empty form, so no reset effect is needed.
 */
import { useState } from "react";
import {
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  toast,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import {
  EMPTY_WAREHOUSE_FORM,
  WarehouseFormFields,
  type WarehouseFormValues,
} from "./warehouse-form-fields.tsx";

/** Friendly line for the inline alert; CONFLICT = the name-taken guard. */
function saveErrorMessage(err: unknown): string {
  const e = err as { data?: { code?: string } | null; message?: string };
  if (e?.data?.code === "CONFLICT") return "A warehouse with this name already exists.";
  return e?.message || "Save failed — try again.";
}

export function WarehouseCreateDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Called with the new warehouse's id after the list cache is refreshed. */
  onCreated: (id: string) => void;
}) {
  const utils = trpc.useUtils();
  const create = trpc.warehouses.create.useMutation();

  const [values, setValues] = useState<WarehouseFormValues>(EMPTY_WAREHOUSE_FORM);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onPatch = <K extends keyof WarehouseFormValues>(key: K, value: WarehouseFormValues[K]) => {
    setValues((v) => ({ ...v, [key]: value }));
    setError(null);
  };

  const save = async () => {
    if (!values.name.trim()) return;
    setError(null);
    setSaving(true);
    try {
      const result = await create.mutateAsync({
        name: values.name.trim(),
        code: values.code.trim() || undefined,
        description: values.description.trim() || undefined,
        warehouseType: values.warehouseType,
        isDefault: values.isDefault,
      });
      // Invalidate BEFORE announcing the id — the caller's deferred select
      // waits for the refreshed list to contain the new row (Radix Select
      // falls back to the placeholder for a value it can't find).
      await utils.warehouses.list.invalidate();
      toast.success(`${values.name.trim()} created`, {
        description: "Warehouse added to Inventory.",
      });
      onCreated(result.id);
      onOpenChange(false);
    } catch (err) {
      setError(saveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>New warehouse</DialogTitle>
          <DialogDescription>
            A stock location — godown, physical store, or a virtual bucket. Only the name is
            required.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4 py-1"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          {error ? (
            <div
              role="alert"
              className="border border-rose-200 bg-rose-50 text-bad rounded-md p-3 text-sm"
            >
              {error}
            </div>
          ) : null}

          <WarehouseFormFields values={values} onPatch={onPatch} mode="create" />

          <DialogFooter>
            <Button intent="ghost" size="md" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button intent="primary" size="md" type="submit" disabled={saving || !values.name.trim()}>
              {saving ? "Saving…" : "Save warehouse"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}
